import React, { useEffect, useState, useCallback } from "react";
import { useSetAtom } from "jotai";
import { nanoid } from "nanoid";
import { editingElementIdAtom } from "state-atoms";
import useCurrentAndPrev from "frontend/hooks/use-cur-and-prev";
import consts from "shared/consts";
import { TASK_HEIGHT, TASK_WIDTH } from "shared/datamodel/task";
import { TaskElement } from "../schema";
import { ITaskController } from "../controller";
import useDebounceCallback from "frontend/hooks/use-debounce-callback";

import { EditableText } from "frontend/editableText";
const MemoizedTextEditor = React.memo(EditableText);

export function TaskTitle({ controller }: { controller: ITaskController }) {
  const setEditingElementId = useSetAtom(editingElementIdAtom);
  const textState = useCurrentAndPrev<string>();
  const [sequenceId, setSequenceId] = useState<string | null>(null);

  const { element } = controller;
  const { width = TASK_WIDTH, title, fontSize, textColor, font, align, fontProps, text } = element;

  const reportEventChanged = useDebounceCallback((field: string) => {
    controller.reportFieldChanged(field, "canvas");
  }, 700);

  useEffect(() => {
    if (controller.isEditing()) {
      const newSequenceId = nanoid(8);
      setSequenceId(newSequenceId);
      controller.startActionSequence(newSequenceId);
      textState(text ?? "");
    } else {
      const [previous, current] = textState();
      if (previous !== current && current !== null && sequenceId) {
        controller.endActionSequence(sequenceId);

        // Track title set event (empty to non-empty)
        const wasEmpty = !previous || previous.trim() === "";
        const isNowEmpty = !current || current.trim() === "";
        if (wasEmpty && !isNowEmpty) {
          controller.reportTitleSet("canvas");
        }
      }
      setSequenceId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller.isEditing()]);

  const onChange = useCallback(
    (newTextValue: string) => {
      textState(newTextValue);
      const { id } = controller;
      controller.patchElement(
        id,
        (draft: TaskElement) => {
          draft.title = newTextValue;
        },
        sequenceId ?? undefined
      );
      reportEventChanged("title");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controller, sequenceId, textState]
  );

  const onStopEditing = useCallback(() => {
    setEditingElementId(null);
  }, [setEditingElementId]);

  const onDblClick = useCallback(() => {
    const { id } = controller;
    setEditingElementId(id);
  }, [controller, setEditingElementId]);

  return (
    <MemoizedTextEditor
      width={width - 48}
      height={TASK_HEIGHT - 20}
      text={title}
      isFixedHeight={true}
      isEditing={controller.isEditing()}
      onChange={onChange}
      onStopEditing={onStopEditing}
      onDblClick={onDblClick}
      placeholder={"Type something..."}
      fontSize={Number.parseInt(fontSize as string) ?? consts.DEFAULTS.FONTSIZE}
      fill={textColor ?? consts.DEFAULTS.TEXT_COLOR}
      font={font}
      align={align}
      verticalAlign="top"
      fontProps={fontProps}
      listening
    />
  );
}
